import React, { useEffect, useState, lazy, useMemo } from 'react';
import { HashRouter, Redirect, useLocation } from 'react-router-dom';

import CodeSplit from '../../components/common/code.split';
import { AuthLoading } from '../../components/common';
import USER_TYPES from '@alice/shared/constants/user.types';

import Login from '../login';
import styles from './styles.module.scss';

const Admin = lazy(() => import('./admin'));
const Partner = lazy(() => import('./partner'));
const Customer = lazy(() => import('./customer'));

function ProtectedRoutes({ user, clearSession }) {
  if (!user) {
    return null;
  }

  if (user.type === USER_TYPES.support) {
    return CodeSplit(Admin, { user, clearSession });
  }

  if (user.type === USER_TYPES.provider && user.providerId) {
    return CodeSplit(Partner, { user, clearSession });
  }

  return CodeSplit(Customer, { user, clearSession });
}

function AuthRouter({ user, authState, clearSession, initialDataLoaded }) {
  const location = useLocation();
  const isLoading =
    authState === 'loading' ||
    (authState === 'authenticated' && !initialDataLoaded);

  if (isLoading) {
    return <AuthLoading />;
  }

  if (authState === 'unauthenticated') {
    if (location.pathname !== '/login') {
      return <Redirect to="/login" />;
    }
    return <Login />;
  }

  if (authState === 'authenticated' && user) {
    if (location.pathname === '/login') {
      return <Redirect to="/" />;
    }
    return <ProtectedRoutes user={user} clearSession={clearSession} />;
  }

  return null;
}

export default function BaseRouter({
  user = null,
  getUsers,
  clearSession = () => null,
  syncUserData,
}) {
  const [authState, setAuthState] = useState('loading');
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const userId = useMemo(() => user?._id, [user]);

  useEffect(() => {
    syncUserData()
      .then((userData) => {
        if (!userData) {
          throw new Error('User not found');
        }
        setAuthState('authenticated');
      })
      .catch(() => {
        setAuthState('unauthenticated');
      });
  }, []);

  useEffect(() => {
    if (!user && !initialDataLoaded) {
      return;
    }
    if (!userId) {
      setAuthState('unauthenticated');
      setInitialDataLoaded();
      return;
    }

    if (user.type === USER_TYPES.support) {
      getUsers().finally(() => {
        setAuthState('authenticated');
        setInitialDataLoaded(true);
      });
    } else {
      setAuthState('authenticated');
      setInitialDataLoaded(true);
    }
  }, [userId]);

  return (
    <div className={styles.root}>
      <HashRouter>
        <AuthRouter
          user={user}
          authState={authState}
          clearSession={clearSession}
          initialDataLoaded={initialDataLoaded}
        />
      </HashRouter>
    </div>
  );
}
